import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useState } from 'react';
import { useAppDispatch } from "@store/hooks";
import { createPaper } from "@features/workspace-papers/state/workspacePapersSlice";

interface CreatePaperModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

export function CreatePaperModal({ open, onClose, workspaceId }: CreatePaperModalProps) {
  const dispatch = useAppDispatch();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [paperName, setPaperName] = useState('');

  const handleCreate = async () => {
    if (!paperName.trim()) {
      setError('Please enter a paper title');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await dispatch(
        createPaper({ workspaceId, paperName: paperName.trim() })
      ).unwrap();

      // Success - close and reset
      onClose();
      setPaperName('');
      setError('');
    } catch (err: unknown) {
      console.error('Failed to create paper:', err);
      setError(err instanceof Error ? err.message : 'Failed to create paper');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
      setPaperName('');
      setError('');
    }
  };

  const isValid = paperName.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Paper</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Paper Title"
          value={paperName}
          onChange={(e) => setPaperName(e.target.value)}
          sx={{ mt: 2 }}
          required
          disabled={submitting}
          autoFocus
          inputProps={{ maxLength: 255 }}
          helperText="Enter a title for your paper"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!isValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
